// Demo-tenant watermark.
//
// When (and only when) a user is signed in to the Turner Kitchens demo tenant,
// overlay a faint, repeating diagonal watermark across the whole portal showing
// the signed-in user's email + today's date. This brands demo screen-shares /
// recordings as non-production. It is purely cosmetic, non-interactive
// (pointer-events:none) and must never appear for any other tenant.

import { onAuthChange } from "./auth";
import type { AuthUser } from "./types";

// Tenants that get the watermark. Matched on the public-facing slug, which is
// stable and present on AuthUser. Add more demo slugs here if ever needed.
//
// 2026-07-01: temporarily disabled for the Häcker meeting; restored
// 2026-10-11 (Steve).
const DEMO_TENANT_SLUGS = new Set<string>(["turner-kitchens"]);

const OVERLAY_ID = "tkos-demo-watermark";

function isDemoTenant(user: AuthUser | null): boolean {
  if (!user) return false;
  const slug = (user.retailerSlug ?? "").trim().toLowerCase();
  return DEMO_TENANT_SLUGS.has(slug);
}

function watermarkLabel(user: AuthUser): string {
  const who = (user.email ?? "").trim() || user.retailerName || "Demo";
  const date = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return `${who}  ·  ${date}`;
}

// Build a tiled SVG background: one faint, 45°-rotated label per tile. Repeating
// the tile yields a continuous diagonal watermark across any-sized viewport.
function buildBackgroundImage(label: string): string {
  const tileW = 360;
  const tileH = 200;
  const xmlText = label
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${tileW}' height='${tileH}'>` +
    `<text x='${tileW / 2}' y='${tileH / 2}' fill='#ffffff' fill-opacity='0.06' ` +
    `font-family='system-ui,-apple-system,Segoe UI,sans-serif' font-size='15' font-weight='600' ` +
    `text-anchor='middle' transform='rotate(-45 ${tileW / 2} ${tileH / 2})'>${xmlText}</text>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

function ensureOverlay(): HTMLElement {
  let el = document.getElementById(OVERLAY_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = OVERLAY_ID;
    el.setAttribute("aria-hidden", "true");
    // Inline the structural styles so the overlay works even if a page's CSS
    // hasn't loaded yet. Fixed, full-viewport, non-interactive, on top.
    el.style.position = "fixed";
    el.style.inset = "0";
    el.style.zIndex = "2147483646"; // just under absolute max so true modals can still sit above if needed
    el.style.pointerEvents = "none";
    el.style.backgroundRepeat = "repeat";
    el.style.userSelect = "none";
    document.body.appendChild(el);
  }
  return el;
}

function removeOverlay(): void {
  document.getElementById(OVERLAY_ID)?.remove();
}

function render(user: AuthUser | null): void {
  if (!isDemoTenant(user) || !user) {
    removeOverlay();
    return;
  }
  const el = ensureOverlay();
  el.style.backgroundImage = buildBackgroundImage(watermarkLabel(user));
}

/**
 * Mount the demo watermark. Renders immediately for the current user and keeps
 * itself in sync with auth changes (login / logout / /portal/me refresh, which
 * is when the email typically arrives). Returns a cleanup function.
 */
export function mountDemoWatermark(initialUser: AuthUser | null): () => void {
  render(initialUser);
  const off = onAuthChange((user) => render(user));
  return () => {
    off();
    removeOverlay();
  };
}
